// src/hooks/useGoogleMapsDistance.js
import { useCallback, useState } from "react";
import { apiMapsDistance } from "../services/mapsConfigApi";

/**
 * Hook para calcular distância entre dois pontos usando Google Maps Distance Matrix API
 * @returns {Object} { calcularDistancia, loading, error }
 */
export function useGoogleMapsDistance() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  /**
   * Calcula distância entre origem e destino
   * @param {Object} origin - { lat, lng } ou string de endereço
   * @param {Object} destination - { lat, lng } ou string de endereço
   * @returns {Promise<Object>} { distanceKm, distanceMeters, duration, durationText }
   */
  const calcularDistancia = useCallback(async (grupoId, origin, destination) => {
    setLoading(true);
    setError("");

    try {
      if (!grupoId) throw new Error("grupoId não informado");

      const result = await apiMapsDistance({ grupoId, origin, destination });
      const distanceMeters = result.distanceMeters || 0;
      const distanceKm = (distanceMeters / 1000).toFixed(1);

      setLoading(false);
      return {
        distanceKm: parseFloat(distanceKm),
        distanceMeters,
        duration: result.duration || 0,
        durationText: result.durationText || "",
        distanceText: result.distanceText || "",
      };
    } catch (err) {
      setLoading(false);
      const errorMsg = err.message || "Erro ao calcular distância";
      setError(errorMsg);
      throw err;
    }
  }, []);

  /**
   * Gera URL do Google Maps para navegação
   * @param {Object} destination - { lat, lng } ou string de endereço
   * @param {Object} origin - (opcional) { lat, lng } ou string de endereço
   * @returns {string} URL para abrir no Google Maps
   */
  const gerarUrlGoogleMaps = useCallback((destination, origin = null) => {
    const baseUrl = "https://www.google.com/maps/dir/";
    
    let url = baseUrl;

    if (origin) {
      if (typeof origin === "string") {
        url += encodeURIComponent(origin);
      } else if (origin.lat && origin.lng) {
        url += `${origin.lat},${origin.lng}`;
      }
      url += "/";
    }

    if (typeof destination === "string") {
      url += encodeURIComponent(destination);
    } else if (destination.lat && destination.lng) {
      url += `${destination.lat},${destination.lng}`;
    }

    return url;
  }, []);

  return {
    calcularDistancia,
    gerarUrlGoogleMaps,
    loading,
    error,
  };
}
